import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import { AccountClearingControlSummary } from '../../../../../../../../../features/registration/account/controls/account-clearing-controls-summary';
import { RegistrationAccountOverviewTabbedNavigation } from '../../../../../../../../../features/registration/account/registration-account-overview-tabbed-navigation';
import { RegistrationClearingAccountUpdateControlsDrawer } from '../../../../../../../../../features/registration/account/registration-clearing-account-update-controls-drawer';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_overview/detail/clearing-controls')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-clearing-controls`,
        label: 'Account Details',
        to: `/registration/account/${params.accountId}/detail/clearing-controls`,
      }),
    }) satisfies AggregateBreadcrumb,
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountOverview,
      label: 'Clearing Controls',
      order: 4,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountOverview>,
  head: () => getPageHead('Account Clearing Controls'),
  component: AccountClearingControlDetails,
});

function AccountClearingControlDetails() {
  const { accountId } = Route.useParams();
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const showPending = Boolean(isPending || error);

  return (
    <VerticalStack gap={7}>
      <RegistrationAccountOverviewTabbedNavigation
        accountId={accountId}
        actions={
          activeTenantIsClearing ? (
            <RequiresAccess write>
              <RegistrationClearingAccountUpdateControlsDrawer accountId={accountId} trigger={<Button size="small">Edit</Button>} />
            </RequiresAccess>
          ) : undefined
        }
      />

      <AccountClearingControlSummary controls={data?.account?.data?.clearingControls} isPending={showPending} programId={data?.account?.programId} />
    </VerticalStack>
  );
}
